import { Responsive } from '@cian/ui-kit';
import { MobileSlidingContainer } from '@cian/frontend-mobile-sliding-container';
import classNames from 'classnames';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as style from './index.css';
import { setEditableMessage } from '../../actions/chat/actions';
import { EInternetStatus, setInternetStatus, TInternetStatus } from '../../actions/internetStatus';
import { updateMessages } from '../../actions/messages/updateMessages';
import { ChatsErrorPage } from '../../components/ChatsErrorPage';
import { MobileLayout } from '../../components/MobileLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';
import {
  currentOpenChatIDSelector,
  currentOpenChatSelector,
  internetStatusSelector,
  isMasterSelector,
  userSelector,
} from '../../selectors';
import { checkOnline } from '../../services/checkOnline';
import { checkUsersSession } from '../../services/checkUsersSession';
import { EChatbotPayload } from '../../services/сhatbot/fetchChatbotActionButtons';
// import { setBlinkingTitleWithTimeout } from '../../services/setBlinkingTitle';
import { setSocketStatusPending } from '../../slices/socketStatusSlice';
import { startPollingMessages, stopPollingMessages } from '../../thunks/chatbot';
import { getChatbotActionButtons } from '../../thunks/chatbot/getChatbotActionButtons';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { TAppState } from '../../types/appState';
import { EHostType } from '../../types/hostType';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../../utils/applicationContext';
import { isSubAgentsChatsEnabled } from '../../utils/isSubAgentsChatsEnabled';
import { useAnchorTargetBlank } from '../../utils/useAnchorTargetBlank';
import { AlertContainer } from '../AlertContainer';
import { AuthNotificationContainer } from '../AuthNotificationContainer';
import { ChatActionModalContainer } from '../ChatActionModalContainer';
import { ChatListContainer } from '../ChatListContainer';
import { ChatWindowContainer } from '../ChatWindowContainer';
import { ChatbotFiltersModalContainer } from '../ChatbotFiltersModalContainer';
import { CustomActionModalContainer } from '../CustomActionModalContainer';
import { DailyrentBookingCalendarSettingsModalContainer } from '../DailyrentBookingCalendarSettingsModalContainer';
import { DailyrentReviewModalContainer } from '../DailyrentReviewModalContainer';
import { DeleteMessageModalContainer } from '../DeleteMessageModalContainer';
import { FeedbackChatbotModalContainer } from '../FeedbackChatbotModalContainer';
import { MasterTabsContainer } from '../MasterTabsContainer';
import { UserCardContainer } from '../UserCardContainer';
import { BottomNavigationContainer } from '../BottomNavigationContainer';
import { useMobileBottomNavigation } from '../../hooks/useMobileBottomNavigationEnabled';

export const PageContainer: React.FC = () => {
  const {
    config,
    logger,
    telemetry,
    custom: { hostType },
  } = useApplicationContext();

  const dispatch = useDispatch();

  useAnchorTargetBlank();
  const activeSide = useSelector((state: TAppState) => state.mobileSide);
  const showErrorPage = useSelector((state: TAppState) => state.showErrorPage);
  const { isAuthenticated } = useSelector(userSelector);
  const internetStatus = useSelector(internetStatusSelector);
  const currentOpenChatID = useSelector(currentOpenChatIDSelector);
  const isChatbotActive = currentOpenChatID === CHATBOT_ID;
  const isChatOpen = currentOpenChatID !== undefined;
  const currentChat = useSelector(currentOpenChatSelector);
  const isMaster = useSelector(isMasterSelector);
  const isMobileBottomNavigationEnabled = useMobileBottomNavigation();
  const isMasterTabsEnabled = isMaster && isSubAgentsChatsEnabled(config);
  const isDesktopFrame = hostType === EHostType.DesktopFrame;

  const { editableMessage, defaultMessagePayload } = currentChat || {};

  const connectionCallback = React.useCallback(
    async (event: Event) => {
      dispatch(setInternetStatus(event.type as TInternetStatus));

      if (event.type === EInternetStatus.Online) {
        dispatch(setSocketStatusPending());
        dispatch(checkUsersSession());
      }
    },
    [dispatch],
  );

  /**
   * TODO: решение временное (хз сколько лет уже), сообщать о том, что пользователь разлогинился должен бэкенд.
   * Каждый раз, когда происходит фокус окна, запрашиваем токен сессии.
   * Если вернулась ошибка, то предполагаем, что пользователь разлогинился.
   * Если оффлайн или показана страница ошибки, делаем запрос после изменения этих флагов
   */
  const focusHandler = React.useCallback(async () => {
    if (internetStatus === EInternetStatus.Offline || showErrorPage) {
      return;
    } else {
      dispatch(checkUsersSession());

      if (!isChatbotActive && currentOpenChatID) {
        dispatch(updateMessages(currentOpenChatID, true));
      }
    }
  }, [internetStatus, showErrorPage, dispatch]);

  const handeRetry = React.useCallback(() => {
    window.location.reload();
  }, []);

  React.useEffect(() => {
    window.addEventListener('focus', focusHandler);

    return () => {
      window.removeEventListener('focus', focusHandler);
    };
  }, [focusHandler, dispatch]);

  React.useEffect(() => {
    const checkOnlinePeriod = config.get<number>('frontend-chats.checkOnlinePeriod') || 30000;
    const checkOnlineUrls = config.get<string[]>('frontend-chats.checkOnlineUrls') || [];

    const clearOnlineChecker = checkOnline({
      period: checkOnlinePeriod,
      urls: checkOnlineUrls,
      onOnline: () => connectionCallback({ type: EInternetStatus.Online } as Event),
      onOffline: () => connectionCallback({ type: EInternetStatus.Offline } as Event),
    });

    return clearOnlineChecker;
  }, [config, connectionCallback]);

  React.useEffect(() => {
    if (showErrorPage) {
      if (logger) {
        logger.error('showErrorPage', {
          domain: 'src/shared/index.tsx',
          message: 'application started with error',
        });
      }

      if (telemetry) {
        telemetry.increment(ETelemetryEvents.ChatPageShowErrorPage);
      }
    }
  }, [logger, showErrorPage, telemetry]);

  React.useEffect(() => {
    if (hostType !== EHostType.Frame || isChatbotActive) {
      dispatch(startPollingMessages({ isQuick: true }));
    }

    return () => {
      dispatch(stopPollingMessages());
    };
  }, [dispatch, hostType, isChatbotActive]);

  React.useEffect(() => {
    const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled') || false;

    if (!isChatbotGPTEnabled) {
      return;
    }

    if (isChatbotActive) {
      dispatch(getChatbotActionButtons(EChatbotPayload.Initial));

      if (editableMessage && defaultMessagePayload) {
        const offerId = parseInt(defaultMessagePayload, 10);
        dispatch(sendChatbotMessage(editableMessage, offerId));
        dispatch(setEditableMessage({ chatId: CHATBOT_ID, text: '' }));
      }

      return;
    }

    if (isChatOpen) {
      dispatch(getChatbotActionButtons(EChatbotPayload.Closing));
    }
  }, [dispatch, config, isChatbotActive, isChatOpen]);

  if (showErrorPage) {
    return (
      <div className={style['chats_container']}>
        <ChatsErrorPage onRetry={handeRetry} />
      </div>
    );
  }

  const containerClassName = classNames(
    style['chats_container'],
    isMobileBottomNavigationEnabled && style['with_bottom_navigation'],
    isDesktopFrame && style['chats_container--desktop_frame'],
  );

  return (
    <>
      <Responsive phone tablet>
        <div className={containerClassName}>
          <MobileLayout
            activeSide={activeSide}
            leftView={<ChatListContainer />}
            centerView={<ChatWindowContainer />}
            rightView={<UserCardContainer />}
          />
          <AlertContainer />
          <ChatActionModalContainer />
          <CustomActionModalContainer />
          <FeedbackChatbotModalContainer />
        </div>
      </Responsive>
      <Responsive desktop>
        <div className={containerClassName}>
          <div className={style['list']}>{isMasterTabsEnabled ? <MasterTabsContainer /> : <ChatListContainer />}</div>
          <div className={style['chat']}>
            {isAuthenticated || isChatbotActive ? <ChatWindowContainer /> : <AuthNotificationContainer />}
            <AlertContainer />
          </div>
          <UserCardContainer />
          <ChatActionModalContainer />
          <CustomActionModalContainer />
          <FeedbackChatbotModalContainer />
        </div>
      </Responsive>
      <ChatbotFiltersModalContainer />
      <DailyrentReviewModalContainer />
      <DailyrentBookingCalendarSettingsModalContainer />
      <DeleteMessageModalContainer />
      <MobileSlidingContainer>
        <BottomNavigationContainer isEnabled={isMobileBottomNavigationEnabled} />
      </MobileSlidingContainer>
    </>
  );
};
